import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { AttachmentContentService } from './attachment-content.service';
import { AinAttachmentItem } from './attachment.model';

@Component({
  standalone: true,
  selector: 'ain-attachments-panel',
  imports: [ButtonModule, DialogModule],
  template: `
    @if (disabled) {
      <p class="muted">{{ disabledMessage || t('shared.attachments.saveFirst') }}</p>
    } @else {
      <div class="attachments">
        @if (canUpload) {
          <div class="attachments-toolbar">
            <input #fileInput type="file" hidden (change)="onFileSelected($event)" />
            <button
              pButton
              type="button"
              [label]="uploadLabel || t('shared.attachments.upload')"
              [disabled]="uploading"
              (click)="fileInput.click()">
            </button>
          </div>
        }

        @if (items.length === 0) {
          <p class="muted">{{ emptyMessage || t('shared.attachments.empty') }}</p>
        } @else {
          <ul>
            @for (item of items; track item.id) {
              <li>
                <button type="button" class="file-name" (click)="openAttachment(item)">{{ item.fileName }}</button>
                <div class="actions">
                  <button pButton type="button" text="true" size="small" [label]="t('shared.attachments.view')" (click)="openAttachment(item)"></button>
                  <button pButton type="button" text="true" size="small" [label]="t('shared.attachments.download')" (click)="downloadAttachment(item)"></button>
                  @if (canDelete) {
                    <button pButton type="button" text="true" size="small" severity="danger" [label]="t('common.delete')" (click)="deleteItem.emit(item.id)"></button>
                  }
                </div>
              </li>
            }
          </ul>
        }
      </div>
    }

    <p-dialog
      [header]="previewTitle()"
      [visible]="previewOpen()"
      [modal]="true"
      [style]="{ width: 'min(960px, 96vw)' }"
      [contentStyle]="{ overflow: 'auto' }"
      (visibleChange)="onPreviewVisibleChange($event)">
      @if (previewLoading()) {
        <p class="muted">{{ t('common.loading') }}</p>
      } @else if (previewError()) {
        <p class="message error">{{ previewError() }}</p>
        @if (previewItem()) {
          <button pButton type="button" [label]="t('shared.attachments.download')" (click)="downloadAttachment(previewItem()!)"></button>
        }
      } @else if (previewMode() === 'pdf') {
        <iframe class="preview-frame" [src]="safePreviewUrl()" [title]="previewTitle()"></iframe>
      } @else if (previewMode() === 'image') {
        <img class="preview-image" [src]="safePreviewUrl()" [alt]="previewTitle()" />
      } @else if (previewMode() === 'text') {
        <pre class="preview-text">{{ previewText() }}</pre>
      }

      <div class="dialog-actions">
        @if (previewItem()) {
          <button pButton type="button" severity="secondary" [label]="t('shared.attachments.download')" (click)="downloadAttachment(previewItem()!)"></button>
        }
        <button pButton type="button" [label]="t('common.cancel')" (click)="closePreview()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .attachments { display: grid; gap: 8px; }
    .attachments-toolbar { display: flex; gap: 8px; }
    ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 8px;
    }
    .file-name {
      border: 0;
      background: transparent;
      color: var(--ain-color-primary, #1d4ed8);
      font-weight: 600;
      text-align: start;
      cursor: pointer;
      word-break: break-all;
    }
    .actions { display: flex; flex-wrap: wrap; gap: 2px; justify-content: flex-end; }
    .preview-image { max-width: 100%; max-height: 70vh; display: block; margin: 0 auto; }
    .preview-frame { width: 100%; min-height: 70vh; border: 0; }
    .preview-text {
      white-space: pre-wrap;
      word-break: break-word;
      max-height: 70vh;
      overflow: auto;
      margin: 0;
      font-size: 12px;
      background: #f8fafc;
      border-radius: 8px;
      padding: 10px;
    }
    .dialog-actions { margin-top: 12px; display: flex; justify-content: flex-end; gap: 8px; }
    .message.error { color: #b91c1c; }
    .muted { color: #64748b; margin: 0; }
  `]
})
export class AinAttachmentsPanelComponent {
  private readonly contentService = inject(AttachmentContentService);
  private readonly localization = inject(LocalizationService);
  private readonly sanitizer = inject(DomSanitizer);

  @Input() items: AinAttachmentItem[] = [];
  @Input() canUpload = true;
  @Input() canDelete = true;
  @Input() uploading = false;
  @Input() disabled = false;
  @Input() disabledMessage = '';
  @Input() emptyMessage = '';
  @Input() uploadLabel = '';

  @Output() upload = new EventEmitter<File>();
  @Output() deleteItem = new EventEmitter<string>();

  readonly previewOpen = signal(false);
  readonly previewLoading = signal(false);
  readonly previewError = signal('');
  readonly previewTitle = signal('');
  readonly previewMode = signal<'none' | 'image' | 'pdf' | 'text'>('none');
  readonly previewObjectUrl = signal('');
  readonly previewText = signal('');
  readonly previewItem = signal<AinAttachmentItem | null>(null);
  readonly safePreviewUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.previewObjectUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  t(key: string): string {
    return this.localization.translate(key);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.upload.emit(file);
  }

  openAttachment(item: AinAttachmentItem): void {
    this.previewItem.set(item);
    this.previewTitle.set(item.fileName);
    this.previewError.set('');
    this.previewText.set('');
    this.clearPreviewObjectUrl();
    this.previewOpen.set(true);
    this.previewLoading.set(true);
    this.previewMode.set('none');

    this.resolveBlob(item).pipe(
      switchMap(blob => {
        const contentType = this.contentService.resolveContentType(item, blob);
        if (!this.contentService.canPreviewInline(contentType, item.fileName)) {
          this.contentService.downloadBlob(blob, item.fileName);
          this.closePreview();
          return of(null);
        }

        if (this.contentService.isText(contentType)) {
          return from(blob.text()).pipe(
            switchMap(text => {
              this.previewText.set(text);
              this.previewMode.set('text');
              return of(null);
            })
          );
        }

        const previewBlob = this.contentService.toPreviewBlob(blob, contentType, item.fileName);
        const objectUrl = URL.createObjectURL(previewBlob);
        this.previewObjectUrl.set(objectUrl);
        this.previewMode.set(this.contentService.isPdf(contentType, item.fileName) ? 'pdf' : 'image');
        return of(null);
      }),
      catchError(() => {
        this.previewError.set(this.t('shared.attachments.previewFailed'));
        return of(null);
      })
    ).subscribe({
      complete: () => this.previewLoading.set(false)
    });
  }

  downloadAttachment(item: AinAttachmentItem): void {
    this.resolveBlob(item).subscribe({
      next: blob => this.contentService.downloadBlob(blob, item.fileName),
      error: () => this.previewError.set(this.t('shared.attachments.downloadFailed'))
    });
  }

  onPreviewVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closePreview();
      return;
    }

    this.previewOpen.set(true);
  }

  closePreview(): void {
    this.previewOpen.set(false);
    this.previewLoading.set(false);
    this.previewError.set('');
    this.previewText.set('');
    this.previewMode.set('none');
    this.previewItem.set(null);
    this.clearPreviewObjectUrl();
  }

  private resolveBlob(item: AinAttachmentItem) {
    if (item.file) {
      return of(item.file);
    }

    if (!item.downloadUrl?.trim()) {
      throw new Error('download_url_missing');
    }

    return this.contentService.fetchBlob(item.downloadUrl);
  }

  private clearPreviewObjectUrl(): void {
    const current = this.previewObjectUrl();
    if (current) {
      URL.revokeObjectURL(current);
    }
    this.previewObjectUrl.set('');
  }
}
